import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  statsAPI,
  knowledgeAPI,
  projectAPI,
  categoryAPI,
} from "../../services/api";

import {
  MapPinIcon,
  ChartBarIcon,
  BookOpenIcon,
  UsersIcon,
  HeartIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";

const InformationSection = () => {
  const [stats, setStats] = useState({
    projects: 0,
    knowledge: 0,
    networks: 0,
    satisfaction: 0,
    categories: 0,
    members: 0,
  });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);

      // ตั้งค่า processing สำหรับแต่ละรายการ
      setProcessing({
        projects: true,
        knowledge: true,
        networks: true,
        satisfaction: true,
        categories: true,
        members: true,
      });

      try {
        // ดึงข้อมูลจาก API พร้อมกัน
        const [dashboardRes, categoriesRes, projectsRes] =
          await Promise.allSettled([
            statsAPI.getDashboard(),
            categoryAPI.getAll(),
            projectAPI.getAll({ limit: 1 }),
          ]);

        // ประมวลผล Dashboard Stats
        if (
          dashboardRes.status === "fulfilled" &&
          dashboardRes.value.data?.success
        ) {
          const dashboardData = dashboardRes.value.data.data;
          setStats((prev) => ({
            ...prev,
            projects: dashboardData?.totalProjects || 0,
            knowledge: dashboardData?.totalKnowledge || 0,
            members: dashboardData?.totalMembers || 0,
          }));
          setProcessing((prev) => ({
            ...prev,
            projects: false,
            knowledge: false,
            members: false,
          }));
        }

        // ประมวลผล Categories
        if (
          categoriesRes.status === "fulfilled" &&
          categoriesRes.value.data?.success
        ) {
          const categoriesData = categoriesRes.value.data.data;
          setStats((prev) => ({
            ...prev,
            categories: categoriesData?.length || 0,
          }));
          setProcessing((prev) => ({ ...prev, categories: false }));
        } else {
          // ถ้าไม่มีข้อมูล categories ให้ใช้ค่าเริ่มต้น
          setStats((prev) => ({ ...prev, categories: 4 }));
          setProcessing((prev) => ({ ...prev, categories: false }));
        }

        // ประมวลผล Networks (จากโครงการ)
        if (
          projectsRes.status === "fulfilled" &&
          projectsRes.value.data?.success
        ) {
          const networksCount = projectsRes.value.data.pagination?.total || 0;
          setStats((prev) => ({ ...prev, networks: networksCount }));
          setProcessing((prev) => ({ ...prev, networks: false }));
        }

        // Satisfaction - อาจจะมาจาก API จริงในอนาคต
        // ปัจจุบันใช้ค่าเริ่มต้น
        setStats((prev) => ({ ...prev, satisfaction: 92 }));
        setProcessing((prev) => ({ ...prev, satisfaction: false }));
      } catch (error) {
        console.error("Failed to fetch stats:", error);
        // ถ้า error ให้ใช้ค่าเริ่มต้น
        setStats({
          projects: 95,
          knowledge: 6,
          networks: 12,
          satisfaction: 92,
          categories: 4,
          members: 81,
        });
        setProcessing({
          projects: false,
          knowledge: false,
          networks: false,
          satisfaction: false,
          categories: false,
          members: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // ฟังก์ชันสำหรับแสดงค่าหรือข้อความ "อยู่ระหว่างประมวลผล"
  const renderValue = (key, value, unit = "") => {
    if (processing[key]) {
      return (
        <div className="flex items-center justify-center space-x-2">
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"></div>
        </div>
      );
    }
    return (
      <>
        {value.toLocaleString()}
        {unit && (
          <span className="ml-1 text-lg font-normal text-yellow-300">
            {unit}
          </span>
        )}
      </>
    );
  };

  const cards = [
    {
      id: 1,
      icon: MapPinIcon,
      title: "พื้นที่บริการวิชาการ",
      value: stats.projects,
      unit: "โครงการ",
      link: "/projects",
      color: "from-blue-600 to-blue-800",
      iconColor: "text-yellow-400",
      dataKey: "projects",
    },
    {
      id: 2,
      icon: ChartBarIcon,
      title: "สถิติการบริการวิชาการ",
      value: stats.projects,
      unit: "โครงการ",
      link: "/report",
      color: "from-blue-700 to-blue-900",
      iconColor: "text-yellow-400",
      dataKey: "projects",
    },
    {
      id: 3,
      icon: BookOpenIcon,
      title: "คลังปัญญา",
      value: stats.knowledge,
      unit: "รายการ",
      link: "/knowledge",
      color: "from-blue-600 to-blue-800",
      iconColor: "text-yellow-400",
      dataKey: "knowledge",
    },
    {
      id: 4,
      icon: UsersIcon,
      title: "เครือข่าย",
      value: stats.networks,
      unit: "เครือข่าย",
      link: "/projects",
      color: "from-blue-700 to-blue-900",
      iconColor: "text-yellow-400",
      dataKey: "networks",
    },
    {
      id: 5,
      icon: HeartIcon,
      title: "ร้อยละความพึงพอใจ",
      value: stats.satisfaction,
      unit: "%",
      link: "/report",
      color: "from-blue-600 to-blue-800",
      iconColor: "text-yellow-400",
      dataKey: "satisfaction",
    },
    {
      id: 6,
      icon: FolderIcon,
      title: "หมวดหมู่",
      value: stats.categories,
      unit: "หมวด",
      link: "/knowledge",
      color: "from-blue-700 to-blue-900",
      iconColor: "text-yellow-400",
      dataKey: "categories",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  if (loading) {
    return (
      <section className="relative py-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse-slow"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h1
            className="text-3xl md:text-4xl font-bold text-primary-500 relative inline-block pb-4"
            style={{
              borderBottom: "3px solid #FFBF07",
            }}
          >
            <i className="fas fa-database text-secondary-500 mr-2"></i>
            สารสนเทศ
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4">
            ข้อมูลและสถิติการบริการวิชาการ เพื่อการพัฒนาท้องถิ่นอย่างยั่งยืน
          </p>
        </motion.div>

        {/* Stats Summary - แถบสรุปตัวเลขสำคัญ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4 p-6 mb-12 bg-white rounded-2xl shadow-lg md:grid-cols-4"
        >
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-500 min-h-[2.5rem] flex items-center justify-center">
              {processing.projects ? (
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"></div>
                </div>
              ) : (
                stats.projects.toLocaleString()
              )}
            </div>
            <div className="text-sm text-gray-600">โครงการทั้งหมด</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-500 min-h-[2.5rem] flex items-center justify-center">
              {processing.knowledge ? (
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"></div>
                </div>
              ) : (
                stats.knowledge.toLocaleString()
              )}
            </div>
            <div className="text-sm text-gray-600">องค์ความรู้</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-500 min-h-[2.5rem] flex items-center justify-center">
              {processing.categories ? (
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"></div>
                </div>
              ) : (
                stats.categories.toLocaleString()
              )}
            </div>
            <div className="text-sm text-gray-600">หมวดหมู่</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-500 min-h-[2.5rem] flex items-center justify-center">
              {processing.members ? (
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"></div>
                </div>
              ) : (
                stats.members.toLocaleString()
              )}
            </div>
            <div className="text-sm text-gray-600">บุคลากร</div>
          </div>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {cards.map((card) => (
            <motion.div
              key={card.id}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to={card.link} className="block h-full">
                <div
                  className={`
                  relative h-full p-8 rounded-2xl shadow-xl
                  bg-gradient-to-br ${card.color}
                  hover:from-yellow-500 hover:to-yellow-600
                  transition-all duration-500
                  group cursor-pointer
                  overflow-hidden
                `}
                >
                  {/* Shine Effect */}
                  <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white to-transparent opacity-20"></div>

                  {/* Icon */}
                  <div className="relative z-10 flex justify-center mb-4">
                    <card.icon
                      className={`w-16 h-16 ${card.iconColor} group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 group-hover:text-white`}
                    />
                  </div>

                  {/* Value */}
                  <div className="relative z-10 mb-2 text-4xl font-bold text-center text-white min-h-[4rem] flex items-center justify-center">
                    {processing[card.dataKey] ? (
                      <div className="flex space-x-2">
                        <div className="w-3 h-3 bg-white rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="w-3 h-3 bg-white rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="w-3 h-3 bg-white rounded-full animate-bounce"></div>
                      </div>
                    ) : (
                      <>
                        {card.value.toLocaleString()}
                        <span className="ml-1 text-lg font-normal text-yellow-300 group-hover:text-white">
                          {card.unit}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="relative z-10 text-xl font-semibold text-center text-white">
                    {card.title}
                  </h3>

                  {/* Decorative Circle */}
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white rounded-full opacity-10"></div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default InformationSection;
